import DealFilters from '@/components/all-from-router/_components/deal-filters/deal-filters';
import {
  FilteredDealsProvider,
  useFilteredDealsContext,
} from '@/components/all-from-router/_components/deal-filters/filtered-deals/filtered-deals-provider';
import VirtualizedDealBoard from '@/components/board-from-router/_components/virtualized-deal-board.tsx';
import DisplayButton from '@/components/ui/deal-list/diplay/display-button.tsx';
import type { ViewOption } from '@/components/ui/deal-list/diplay/display-selector.tsx';
import LoadCancelledDealsButton from '@/components/ui/deal-list/load-cancelled-deals-button';
import VirtualizedDealList from '@/components/ui/deal-list/virtualized-deals-list';
import FindInViewFilter from '@/components/ui/find-in-view-filter/find-in-view-filter';
import { useFindInViewFilterContext } from '@/components/ui/find-in-view-filter/find-in-view-filter-provider';
import FullscreenMessage from '@/components/ui/layout/fullscreen-message';
import { HubListHeader } from '@/components/ui/layout/headers';
import HotkeyDialogBoxes from '@/components/ux/all-list-and-board/hotkey-dialog-boxes';
import useAllListAndBoardHotkeys from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys';
import { useDealsListData } from '@/hooks/use-deals-selectors';
import useLocalStorage from '@/hooks/use-local-storage.ts';
import { type DealListItem } from '@/models/types';
import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

export const Route = createFileRoute('/(authenticated)/$hub/deals/')({
  component: Index,
  staticData: {
    keepAlive: true,
  },
});

const AllPage = observer(() => {
  const dealsData = useDealsListData();
  const dealCount = dealsData
    ? Object.values(dealsData).reduce((acc, curr) => acc + curr.count, 0)
    : 0;

  const { openDialog, setOpenDialog, selectedDeal, enableScope } =
    useAllListAndBoardHotkeys();

  const [viewMode, setViewMode] = useLocalStorage<ViewOption>(
    'deals-display-mode',
    'list',
  );

  const { filterCallback, shouldApplyFilteredDealsIds } =
    useFilteredDealsContext();

  const { findInViewQuery } = useFindInViewFilterContext();

  const findInViewFilterCallback = useCallback(
    (deal: DealListItem) => {
      const query = findInViewQuery.toLowerCase();
      const vehicleString = `${deal.vehicle?.vehicle?.year} ${deal.vehicle?.vehicle?.make} ${deal.vehicle?.vehicle?.model} ${deal.vehicle?.vehicle?.trim}`;
      const vin = deal.vehicle?.vehicle?.vin;

      return (
        deal.short_id.toLowerCase().includes(query) ||
        deal.customer_name?.toLowerCase().includes(query) ||
        deal.customer_email?.toLowerCase().includes(query) ||
        vehicleString.toLowerCase().includes(query) ||
        (vin ? vin.toLowerCase().includes(query) : false)
      );
    },
    [findInViewQuery],
  );

  const headerSubRow = (
    <>
      <DealFilters />
      <LoadCancelledDealsButton />
      <DisplayButton viewMode={viewMode} onViewModeChange={setViewMode} />
    </>
  );

  if (dealCount === 0) {
    return (
      <div className="flex flex-col h-screen">
        <HubListHeader
          findInViewFilter={<FindInViewFilter />}
          subRow={headerSubRow}
        />
        <FullscreenMessage>
          <div>{/* Dev note: intentionally blank */}</div>
        </FullscreenMessage>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col h-screen">
        <HubListHeader
          findInViewFilter={<FindInViewFilter />}
          subRow={headerSubRow}
        />

        {viewMode === 'list' ? (
          <VirtualizedDealList
            dealsData={dealsData}
            filterCallback={filterCallback}
            enableScope={enableScope}
            findInViewFilterCallback={findInViewFilterCallback}
          />
        ) : (
          <VirtualizedDealBoard
            dealsData={dealsData}
            filterCallback={filterCallback}
            enableScope={enableScope}
            shouldApplyFilteredDealsIds={shouldApplyFilteredDealsIds}
            findInViewFilterCallback={findInViewFilterCallback}
          />
        )}
      </div>

      <HotkeyDialogBoxes
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedDeal={selectedDeal}
      />
    </div>
  );
});

function Index() {
  return (
    <FilteredDealsProvider>
      <AllPage />
    </FilteredDealsProvider>
  );
}
